"use client";

import { useState, useCallback } from "react";
import { Tabs } from "react-aria-components";
import { useTranslations } from "next-intl";

import type { PaygProductId } from "../payg-pricing-data";
import type { CalculatorMode } from "../PricingTableContext";
import { usePricingTableContext } from "../PricingTableContext";
import { TabList } from "../../core/TabList";
import { PaygCostCalculator } from "./PaygCostCalculator";
import { PricingCalculator } from "./PricingCalculator";

// ---------------------------------------------------------------------------
// Product ↔ Feature mapping
// ---------------------------------------------------------------------------

/** Map a PAYG product to the corresponding subscription feature */
const PAYG_TO_FEATURE: Record<PaygProductId, string> = {
  tts_flash: "text_to_speech",
  tts_multilingual: "text_to_speech",
  stt_scribe: "speech_to_text",
  stt_realtime: "speech_to_text",
  music: "music",
  voice_isolator: "voice_isolator",
  voice_changer: "voice_isolator",
  sound_effects: "sound_effects",
  dubbing: "dubbing",
};

/** Map a subscription product-category feature to the default PAYG product */
const FEATURE_TO_PAYG: Record<string, PaygProductId> = {
  text_to_speech: "tts_flash",
  speech_to_text: "stt_scribe",
  music: "music",
  voice_isolator: "voice_isolator",
  sound_effects: "sound_effects",
  dubbing: "dubbing",
};

/** Extract the best PAYG product from a set of subscription features */
function featuresToPaygProduct(features: Set<string>, fallback: PaygProductId): PaygProductId {
  for (const feature of features) {
    const mapped = FEATURE_TO_PAYG[feature];
    if (mapped) return mapped;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ApiCalculatorProps {
  className?: string;
}

export function ApiCalculator({ className }: ApiCalculatorProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const { calculatorMode: mode, setCalculatorMode: setMode } = usePricingTableContext();

  // Shared state synced via callbacks from child components — track minutes (not slider position)
  // so switching modes preserves the exact minute value across different slider scales
  const [sharedMinutes, setSharedMinutes] = useState(50);
  const [paygProduct, setPaygProduct] = useState<PaygProductId>("tts_flash");
  const [subFeatures, setSubFeatures] = useState<Set<string>>(() => new Set(["text_to_speech"]));

  const toggleItems = [
    { id: "payg", title: t("toggle_payg") },
    { id: "subscription", title: t("toggle_subscription") },
  ];

  // Derive initial values when switching modes
  const handleModeChange = useCallback(
    (newMode: CalculatorMode) => {
      if (newMode === "subscription") {
        // Map current PAYG product → subscription feature
        const feature = PAYG_TO_FEATURE[paygProduct];
        setSubFeatures(new Set([feature]));
      } else {
        // Map subscription features → PAYG product
        setPaygProduct(featuresToPaygProduct(subFeatures, paygProduct));
      }
      setMode(newMode);
    },
    [paygProduct, subFeatures, setMode],
  );

  return (
    <div className={className}>
      <Tabs selectedKey={mode} onSelectionChange={key => handleModeChange(key as CalculatorMode)}>
        <div className='tw-flex tw-justify-center tw-mb-8'>
          <TabList items={toggleItems} showRing size='lg' />
        </div>
      </Tabs>

      {mode === "payg" ? (
        <PaygCostCalculator
          initialMinutes={sharedMinutes}
          initialProductId={paygProduct}
          onMinutesChange={setSharedMinutes}
          onProductChanged={setPaygProduct}
        />
      ) : (
        <PricingCalculator
          variant='default'
          initialMinutes={sharedMinutes}
          initialFeatures={subFeatures}
          onMinutesChange={setSharedMinutes}
          onFeaturesChanged={setSubFeatures}
        />
      )}
    </div>
  );
}
